import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchAuditForResource } from "../../api/auditApi";
import { Log } from "../../models/Log";

interface ResourceAuditDialogProps {
  resourceId: string;
  isOpen: boolean;
  onClose: () => void;
  count?: number;
  resourceName?: string;
}

const ResourceAuditDialog: React.FC<ResourceAuditDialogProps> = ({
  resourceId,
  isOpen,
  onClose,
  count = 10,
  resourceName,
}) => {
  const {
    data: auditEntries = [],
    isFetching,
    error,
  } = useQuery<Log[]>(
    ["ResourceAudit", resourceId, count],
    () => fetchAuditForResource(resourceId, count),
    {
      enabled: isOpen && !!resourceId,
    }
  );

  const highlightDifferences = (
    oldValues: string,
    newValues: string,
    action: string
  ) => {
    // Handle create operations - show all new values
    if (
      (!oldValues || oldValues === "{}") &&
      newValues &&
      action?.toLowerCase() === "create"
    ) {
      try {
        const newEntries = JSON.parse(newValues);
        return (
          <div className="grid grid-cols-1 gap-2">
            <div className="bg-green-50 p-3 rounded border-l-4 border-green-400">
              <h5 className="font-semibold text-green-800 mb-2">Created Values:</h5>
              {Object.keys(newEntries)
                .filter((key) => newEntries[key]) // Skip empty values
                .map((key) => (
                  <p key={key} className="mb-1">
                    <strong>{key}:</strong>{" "}
                    <span className="font-medium text-green-700">{newEntries[key]}</span>
                  </p>
                ))}
            </div>
          </div>
        );
      } catch (e) {
        return <p className="text-red-500">Error parsing create data</p>;
      }
    }

    // Handle update operations - show differences side by side
    if (!oldValues || !newValues) return null;

    try {
      const oldEntries = JSON.parse(oldValues);
      const newEntries = JSON.parse(newValues);

      const changedKeys = Object.keys(newEntries).filter((key) => {
        const oldValue = oldEntries[key] || "";
        const newValue = newEntries[key] || "";
        return newValue && oldValue !== newValue;
      });

      const unchangedKeys = Object.keys(newEntries).filter((key) => {
        const oldValue = oldEntries[key] || "";
        const newValue = newEntries[key] || "";
        return newValue && oldValue === newValue;
      });

      return (
        <div className="space-y-4">
          {/* Changed Values - Side by Side */}
          {changedKeys.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-red-50 p-3 rounded border-l-4 border-red-400">
                <h5 className="font-semibold text-red-800 mb-2">Old Values:</h5>
                {changedKeys.map((key) => (
                  <p key={key} className="mb-1">
                    <strong>{key}:</strong>{" "}
                    <span className="text-red-700">{oldEntries[key] || "—"}</span>
                  </p>
                ))}
              </div>
              <div className="bg-[#EDF6FF] p-3 rounded border-l-4 border-[#3B82F6]">
                <h5 className="font-semibold text-[#1D4ED8] mb-2">New Values:</h5>
                {changedKeys.map((key) => (
                  <p key={key} className="mb-1">
                    <strong>{key}:</strong>{" "}
                    <span className="font-bold text-[#2563EB]">{newEntries[key]}</span>
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Unchanged Values */}
          {unchangedKeys.length > 0 && (
            <div className="bg-[var(--sc-bg)] p-3 rounded border-l-4 border-[var(--sc-muted)]">
              <h5 className="font-semibold text-[var(--sc-text)] mb-2">Unchanged Values:</h5>
              {unchangedKeys.map((key) => (
                <p key={key} className="mb-1">
                  <strong>{key}:</strong>{" "}
                  <span className="text-[var(--sc-muted)]">{newEntries[key]}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      );
    } catch (e) {
      return <p className="text-red-500">Error parsing audit data</p>;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center pt-20 z-[100]">
      <div className="bg-[var(--sc-surface)] rounded-[12px] shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--sc-border)] flex justify-between items-center">
          <h2 className="text-[15px] font-semibold text-[var(--sc-text)]">
            Audit History {resourceName && `- ${resourceName}`}
          </h2>
          <button
            onClick={onClose}
            className="text-[var(--sc-muted)] hover:text-[var(--sc-text)] text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[70vh]">
          {isFetching ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-[var(--sc-muted)]">Loading audit entries...</div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-8">
              Error loading audit entries
            </div>
          ) : auditEntries.length === 0 ? (
            <div className="text-[var(--sc-muted)] text-center py-8">
              No audit entries found for this resource
            </div>
          ) : (
            <div className="space-y-4">
              {auditEntries.map((entry, index) => (
                <div
                  key={entry.id || index}
                  className="rounded-[10px] border border-[var(--sc-border)] p-4 bg-[var(--sc-bg)]"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-[var(--sc-text)]">
                        {entry.action || "Unknown Action"}
                      </h3>
                      <p className="text-sm text-[var(--sc-muted)]">
                        Table: {entry.tableName}
                      </p>
                      {entry.correlationId && (
                        <Link
                          to={`/audit/correlation/${entry.correlationId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#F4A832] hover:text-[#e09620] underline"
                        >
                          Link →
                        </Link>
                      )}
                    </div>
                    <div className="text-right text-sm text-[var(--sc-muted)]">
                      <p>{new Date(entry.created).toLocaleString()}</p>
                      <p>By: {entry.createdBy}</p>
                    </div>
                  </div>

                  {entry.oldValues || entry.newValues ? (
                    <div className="bg-[var(--sc-surface)] p-3 rounded-lg border border-[var(--sc-border)]">
                      <h4 className="font-medium text-[var(--sc-text)] mb-2">
                        {entry.action?.toLowerCase() === "create"
                          ? "Created with:"
                          : "Changes:"}
                      </h4>
                      <div className="text-sm">
                        {highlightDifferences(
                          entry.oldValues || "",
                          entry.newValues || "",
                          entry.action || ""
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-[var(--sc-muted)] text-sm">
                      No change details available
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--sc-border)] flex justify-end">
          <button
            onClick={onClose}
            className="rounded-full px-4 py-2 text-sm font-medium bg-[var(--sc-bg)] text-[var(--sc-muted)] hover:bg-[var(--sc-border)]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResourceAuditDialog;
